/**
 * Public persistence module surface: provider, hooks, slice descriptors, and diagnostics.
 *
 * @packageDocumentation
 */

import { persistentSliceStore } from "./runtime/PersistentSliceStore";
import { authSlice, type AuthState } from "./slices/auth.slice";
import { ALL_PERSISTED_SLICES } from "./slices/registry";
import type { PersistedSliceDescriptor } from "./types";

export { PersistenceProvider } from "./PersistenceProvider";

export { usePersistedSlice, usePersistedSliceSelector, usePersistenceReady, usePersistenceStatus } from "./usePersistedSlice";

export { BIKE_PREFS_DEFAULT, bikePrefsSlice, type BikePrefs } from "./slices/bikePrefs.slice";

export { CAR_PREFS_DEFAULT, carPrefsSlice, type CarPrefs } from "./slices/carPrefs.slice";

export { MAP_PREFS_DEFAULT, mapPrefsSlice, type MapPrefs } from "./slices/mapPrefs.slice";

export { displayCurrencyToIsEuro, isEuroToDisplayCurrency, PRICING_PREFS_DEFAULT, pricingPrefsSlice, type DisplayCurrency, type PricingPrefs } from "./slices/pricingPrefs.slice";

export {
    RENTAL_VEHICLES_PREFS_DEFAULT,
    rentalVehiclesPrefsSlice,
    type RentalVehiclesPrefs,
} from "./slices/rentalVehiclesPrefs.slice";

export {
    FAB_UI_DEFAULT,
    fabUiSlice,
    type FabUiState,
    type SavedListTabId,
    type TransportsPrefsTabId,
} from "./slices/fabUi.slice";

export { LAST_GPS_DATA_DEFAULT, lastGpsDataSlice, type LastGpsDataState } from "./slices/lastGpsData.slice";

export { AUTH_DEFAULT, authSlice, type AuthState } from "./slices/auth.slice";

export { appendSearchHistoryItem, SEARCH_HISTORY_DEFAULT, searchHistorySlice, type SearchHistoryState } from "./slices/searchHistory.slice";

export {
    SAVED_ITINERARIES_DEFAULT,
    savedItinerariesSlice,
    type SavedItinerariesState,
    type SavedItineraryEntry,
} from "./slices/savedItineraries.slice";

export {
    PURCHASED_ITINERARIES_DEFAULT,
    purchasedItinerariesSlice,
    type PurchasedItinerariesState,
    type PurchasedItineraryEntry,
    type PurchasedItineraryPurchase,
    type PurchasedSegmentData,
    type StoredPurchasedTicket,
} from "./slices/purchasedItineraries.slice";

export {
    TRANSIT_PREFS_DEFAULT,
    transitPrefsSlice,
    type MaxTransfersPreference,
    type TransitModeFilter,
    type TransitPrefs,
} from "./slices/transitPrefs.slice";

export { ALL_PERSISTED_SLICES, EAGER_PERSISTED_SLICES } from "./slices/registry";

/**
 * Redacts the access token before including auth state in error reports.
 *
 * @param auth Current auth slice value from `persistentSliceStore.peek`.
 * @returns Plain object safe for telemetry (token replaced with `[REDACTED]`).
 */
const redactAuthToken = ( auth: AuthState ): Record<string, unknown> => {
    return {
        "accessToken":      auth.accessToken == null ? null : "[REDACTED]",
        "pkceCodeVerifier": auth.pkceCodeVerifier == null ? null : "[REDACTED]",
        "oauthState":       auth.oauthState == null ? null : "[REDACTED]",
    };
};

/**
 * Builds a flat read-only snapshot of all persisted slices for diagnostic reporting.
 *
 * @returns Plain object keyed by slice key. Reads from in-memory store without triggering I/O.
 * @remarks Token values are redacted before inclusion in error reports.
 */
export const buildPersistedSlicesSnapshot = (): Record<string, unknown> => {
    const snapshot: Record<string, unknown> = {};

    for ( const slice of ALL_PERSISTED_SLICES ) {
        if ( slice.key === authSlice.key ) {
            snapshot[slice.key] = redactAuthToken( persistentSliceStore.peek( authSlice ) );

            continue;
        }

        snapshot[slice.key] = persistentSliceStore.peek( slice as PersistedSliceDescriptor<unknown> );
    }

    return snapshot;
};
